
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  HostListener,
  Input,
  OnInit, inject, DestroyRef } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { filter, map, shareReplay, tap } from 'rxjs/operators';
import { FieldState, OptionState } from '../../../models';
import { DataFilterAction } from '../../../store/actions';
import { DataManipulationSelectors } from '../../../store/selectors';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-data-filter-option',
  templateUrl: './data-filter-option.component.html',
  styleUrls: ['./data-filter-option.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DataFilterOptionComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private option: OptionState;
  private hasNoRelatedData: boolean;
  private fieldName: string;

  @HostBinding('class.opacity-50')
  @HostBinding('class.pointer-events-none')
  private get isDisabled(): boolean {
    return this.hasNoRelatedData && !this.option.isChecked;
  }

  @Input()
  fieldId: string;

  @Input()
  optionId: string;

  @Input()
  filterInstanceId: string;

  field$: Observable<FieldState>;
  option$: Observable<OptionState>;
  optionDataCount$: Observable<number>;

  constructor(private store: Store, private cd: ChangeDetectorRef, private translate: TranslateService) {}

  ngOnInit(): void {
    this.field$ = this.store.pipe(
      select(DataManipulationSelectors.Filter.createFieldStateSelector(this.filterInstanceId, this.fieldId))
    ).pipe(tap((field) => this.buildFieldName(field.translationKey)));
    this.option$ = this.store.pipe(
      select(DataManipulationSelectors.Filter.createOptionSelector(this.filterInstanceId, this.fieldId, this.optionId)),
      filter((option) => !!option),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.option$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((option) => {
      this.option = option;
      this.cd.detectChanges();
    });
    this.optionDataCount$ = this.store.pipe(
      select(
        DataManipulationSelectors.Filter.createOptionCountCalculationSelector(this.filterInstanceId, this.fieldId, this.optionId)
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    this.optionDataCount$
      .pipe(
        map((count) => !count),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((hasNoRelatedData) => {
        this.hasNoRelatedData = hasNoRelatedData;
        this.cd.markForCheck();
      });
  }

  getValueToTranslate(): string {
    return this.option.translationKey || this.option.value;
  }

  @HostListener('click')
  private toggle(): void {
    this.store.dispatch(
      DataFilterAction.toggleOptions({
        instanceId: this.filterInstanceId,
        optionsData: { [this.fieldId]: [this.option.id] },
        fieldName: this.fieldName
      })
    );
  }

  private buildFieldName(key: string): void {
    this.fieldName = this.translate.instant(key);
  }
}
